import { useDispatch } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import React, { JSX, useEffect, useState } from "react";

import QrPanelLayout from "../QrPanelLayout";
import Overlay from "../../../../../components/Overlay/Overlay";
import { notificationAdd } from "../../../../../actions/notification";
import { BillDocumentType } from "../../../../../interfaces/BillInterfaces";
import useCurrentResourceCentre from "../../../../../hooks/useCurrentResourceCentre";
import { useCurrentStore } from "../../../../../hooks/useCurrentStore";
import { useCurrentCounter } from "../../../../../hooks/useCurrentCounter";
import { TQrPaymentInfo } from "../../../../../interfaces/QrPayment";
import { getFonepayDynamicQrString } from "../../../../../api/bill";
import FonepayPaymentStatus from "./FonepayPaymentStatus";
import FonepayWebSocketStatus from "./FonepayWebSocketStatus";
import { QrPaymentMethodsEnum } from "../../../../../enum/billing";
import { useFonepayWSS } from "../../../../../hooks/useFonePayWSS";
import Qr from "../../../../../components/Qr/Qr";

interface QrPaymentPanelProps {
  draft: BillDocumentType;
  currentBillCounter: string;
  selectedPaymentMethod: keyof TQrPaymentInfo | null;
  onClose: () => void;
}

// eslint-disable-next-line import/prefer-default-export
export function FonepayDynamicQrPanel({
  draft,
  onClose,
  currentBillCounter,
  selectedPaymentMethod
}: QrPaymentPanelProps): JSX.Element {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const { currentStore: storeLabel } = useCurrentStore();
  const { currentCounter: terminalLabel } = useCurrentCounter();

  const [qrString, setQrString] = useState("");
  const [webSocketUrl, setWebSocketUrl] = useState("");
  const [productNumber, setProductNumber] = useState("");

  const paidAmount =
    draft?.paymentInfo?.paymentBreakdown.find(
      (b) => b.method.toLowerCase() === QrPaymentMethodsEnum.FONEPAY.toLowerCase()
    )?.amount ?? 0;

  const { wsStatus, paymentStatus, checkStatus } = useFonepayWSS({
    webSocketUrl,
    productNumber,
    rcId: rc?.id
  });

  useEffect(() => {
    (async () => {
      try {
        if (rc?.id) {
          const { success, data, extraData } = await getFonepayDynamicQrString({
            resourceCentreId: rc.id,
            amount: paidAmount,
            billNumber: String(draft.billNumber)
          });

          if (success) {
            setQrString(data?.qrMessage);
            setWebSocketUrl(data?.thirdpartyQrWebSocketUrl);
            setProductNumber(extraData?.productNumber ?? "");
          }
        }
      } catch (error) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            message:
              (error as Error).message ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error as any)?.data?.message ||
              "Unexpected error occurred",
            autoTimeout: true
          })
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.billNumber, draft.remarks, paidAmount, rc?.id, storeLabel, terminalLabel]);

  return (
    <QrPanelLayout
      title="Pay with Fonepay"
      draft={draft}
      onClose={onClose}
      currentBillCounter={currentBillCounter}
      selectedPaymentMethod={selectedPaymentMethod}
    >
      <Overlay
        text={paymentStatus === "COMPLETED" ? "Payment completed!" : "Processing..."}
        show={paymentStatus !== null && !["PENDING", "FAILED"].includes(paymentStatus)}
      >
        {qrString && <Qr value={qrString} />}
      </Overlay>

      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography>Payment Status:</Typography>
          <FonepayPaymentStatus status={paymentStatus} />
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography>Connection Status:</Typography>
          <FonepayWebSocketStatus status={wsStatus} />
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography>Check Payment Status:</Typography>

          <Button variant="contained" size="small" onClick={checkStatus}>
            Check
          </Button>
        </Box>
      </Box>
    </QrPanelLayout>
  );
}
